import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useI18n } from "../i18n";
import { smoothScrollTo } from "../lib/scroll";
import { useAuth } from "../auth";

export const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const { user, login, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [prestOpen, setPrestOpen] = useState(false);
  const dropRef = useRef(null);

  const PRESTATIONS = [
    { label: t("nav.coveringServices"), href: "#services" },
    { label: t("nav.teintes"), href: "#teintes" },
    { label: t("nav.ppf"), href: "#ppf" },
    { label: t("nav.films"), href: "#films" },
    { label: t("nav.dechromage"), href: "#dechromage" },
    { label: t("nav.vitres"), href: "#vitres" },
    { label: t("nav.polissage"), href: "#polissage" },
  ];

  const LINKS = [
    { label: t("nav.advisor"), href: "#style-advisor" },
    { label: t("nav.realisations"), href: "#realisations" },
    { label: t("nav.pourquoi"), href: "#pourquoi" },
    { label: t("nav.tarifs"), href: "#tarifs" },
    { label: t("nav.reserver"), href: "#reserver" },
    { label: t("nav.devis"), href: "#devis" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setPrestOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const scrollToSection = (href) => smoothScrollTo(href, 80);

  const go = (href) => {
    setOpen(false);
    setPrestOpen(false);
    scrollToSection(href);
  };

  const linkCls =
    "font-mono-accent text-xs uppercase tracking-[0.15em] text-neutral-400 hover:text-cyan transition-colors duration-300 whitespace-nowrap";

  const LangSwitch = ({ testid }) => (
    <div data-testid={testid} className="flex items-center gap-1 font-mono-accent text-xs">
      <button data-testid="lang-fr" onClick={() => setLang("fr")} className={`px-1 py-0.5 uppercase tracking-widest transition-colors duration-300 ${lang === "fr" ? "text-cyan" : "text-neutral-500 hover:text-neutral-300"}`}>FR</button>
      <span className="text-neutral-700">/</span>
      <button data-testid="lang-en" onClick={() => setLang("en")} className={`px-1 py-0.5 uppercase tracking-widest transition-colors duration-300 ${lang === "en" ? "text-cyan" : "text-neutral-500 hover:text-neutral-300"}`}>EN</button>
    </div>
  );

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${scrolled ? "glass border-b border-cyan-500/10" : "bg-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          aria-label="Wrapp Man — Accueil"
        >
          <img
            src="/logo-wrappman.png"
            alt="Wrapp Man"
            className="h-11 w-11 md:h-12 md:w-12 rounded-full object-cover ring-1 ring-cyan-500/40 group-hover:ring-cyan-400 transition-all duration-300"
          />
          <span className="hidden sm:inline font-display font-900 text-xl tracking-tighter uppercase">
            Wrapp<span className="text-cyan">.</span>Man
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-7">
          <div ref={dropRef} className="relative">
            <button
              data-testid="nav-prestations-trigger"
              onClick={() => setPrestOpen((o) => !o)}
              className={`inline-flex items-center gap-1.5 outline-none ${linkCls} ${prestOpen ? "text-cyan" : ""}`}
            >
              {t("nav.prestations")}
              <ChevronDown size={13} className={`transition-transform duration-300 ${prestOpen ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {prestOpen && (
                <motion.div
                  data-testid="nav-prestations-menu"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute top-full left-0 pt-4"
                >
                  <div className="glass bg-[#0F1115]/95 border border-cyan-500/20 rounded-2xl p-2 min-w-[240px] shadow-2xl shadow-black/50">
                    {PRESTATIONS.map((p) => (
                      <button
                        key={p.href}
                        data-testid={`nav-drop-${p.href.replace("#", "")}`}
                        onClick={() => go(p.href)}
                        className="block w-full text-left font-mono-accent text-xs uppercase tracking-[0.12em] text-neutral-300 hover:bg-cyan-400/10 hover:text-cyan rounded-lg px-3 py-2.5 transition-colors duration-200"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {LINKS.map((l) => (
            <button key={l.href} data-testid={`nav-${l.href.replace("#", "")}`} onClick={() => go(l.href)} className={linkCls}>
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <LangSwitch testid="lang-switch-desktop" />
          {user ? (
            <a href="/mon-compte" data-testid="nav-account" className={linkCls}>
              {t("nav.compte")}
            </a>
          ) : (
            <button data-testid="nav-login" onClick={login} className={linkCls}>
              {t("nav.connexion")}
            </button>
          )}
          <button
            data-testid="nav-cta-devis"
            onClick={() => go("#devis")}
            className="inline-flex items-center gap-2 text-black font-medium text-sm px-5 py-2.5 rounded-full hover:brightness-110 transition-all duration-300 whitespace-nowrap"
            style={{ backgroundColor: "#C9A96A" }}
          >
            {t("nav.devisBtn")}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <LangSwitch testid="lang-switch-mobile" />
          <button data-testid="mobile-menu-toggle" className="text-white" onClick={() => setOpen((o) => !o)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden glass border-t border-cyan-500/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-cyan">{t("nav.prestations")}</span>
              {PRESTATIONS.map((p) => (
                <button
                  key={p.href}
                  data-testid={`mobile-nav-${p.href.replace("#", "")}`}
                  onClick={() => go(p.href)}
                  className="font-mono-accent text-sm uppercase tracking-[0.18em] text-neutral-300 text-left pl-3"
                >
                  {p.label}
                </button>
              ))}
              <div className="h-px bg-white/10 my-1" />
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="font-mono-accent text-sm uppercase tracking-[0.18em] text-neutral-300 text-left"
                >
                  {l.label}
                </button>
              ))}
              <div className="h-px bg-white/10 my-1" />
              {user ? (
                <a href="/mon-compte" data-testid="mobile-nav-account" className="font-mono-accent text-sm uppercase tracking-[0.18em] text-cyan text-left">
                  {t("nav.compte")}
                </a>
              ) : (
                <button data-testid="mobile-nav-login" onClick={login} className="font-mono-accent text-sm uppercase tracking-[0.18em] text-cyan text-left">
                  {t("nav.connexion")}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
